import { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useGetPurchaseByMarketerQuery,
  useGetMarketerByUserIdQuery,
  useUpdateTaskStatusMutation,
} from "../features/marketing/marketingApiSlice";

export const businessTypeRoutes = {
  homemadeShop: "/home-made-owner-profile/",
  gemShop: "/minerals-and-mining-profile/",
  services: "/services-profile/",
  businessPropertyCompany: "/Sale-Bussines-Property/",
  company: "/company-profile/",
  jobsCompany: "/find-jobs-online/",
  usedShop: "/Used-Items-Bussines-Profile/",
  rawmaterialShop: "/rawmaterial-owner-profile/",
  manufacturerShop: "/manufacturer-owner-profile/",
  wholesellerShop: "/wholesaler-owner-profile/",
  retailerShop: "/NewItem-bussines-Profile/",
  recycledShop: "/recycle-profile/",
  farmingShop: "/farming-profile/",
  usedVehicleShop: "/used-vehicle-profile/",
  foodShop: "/RestaurantAlldata/",
  transportShop: "/transport-profile/",
};

export const useMyClientsState = () => {
  const [activeClientTab, setActiveClientTab] = useState("active");
  const [activePlanTab, setActivePlanTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [show, setShow] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const navigate = useNavigate();
  const { name } = useSelector((state) => state.user);
  const authUser = useSelector((state) => state.auth?.user);
  const userId = name?._id || authUser?._id || null;

  // Fetch marketer profile directly from backend API using user ID
  const { data: marketerRes, isLoading: isMarketerLoading } = useGetMarketerByUserIdQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: true,
  });

  const marketerData = marketerRes?.data?.[0] || null;
  const marketerId = marketerData?._id || marketerData?.marketerId || userId;

  // Fetch clients directly from backend API using resolved marketerId
  const { data: responseData, isLoading: isPurchaseLoading } = useGetPurchaseByMarketerQuery(
    marketerId,
    {
      skip: !marketerId,
      refetchOnMountOrArgChange: true,
    }
  );

  const isLoading = (isMarketerLoading && !marketerData) || isPurchaseLoading;

  const [updateTaskStatus] = useUpdateTaskStatusMutation();

  const clients = responseData?.data || [];

  // Filter clients based on tab, plan, and search query
  const filteredClients = useMemo(() => {
    if (!clients) return [];

    let filteredByTab = [];

    switch (activeClientTab) {
      case "active":
        // Active = payment confirmed AND work either started or pending start
        filteredByTab = clients.filter(
          (client) =>
            client.paymentStatus === "success" &&
            (client.taskStatus === "inProgress" || client.taskStatus === "pending")
        );
        break;
      case "completed":
        filteredByTab = clients.filter(
          (client) =>
            client.paymentStatus === "success" &&
            client.taskStatus === "completed"
        );
        break;
      case "inactive":
        // Inactive = payment not yet confirmed (pending/failed)
        filteredByTab = clients.filter(
          (client) => client.paymentStatus !== "success"
        );
        break;
      default:
        filteredByTab = clients;
    }

    // Filter by plan.
    // BUG FIXED: clients sourced from an approved HireRequest (no purchased
    // plan yet) come back with `planId: null` from the backend, so
    // `client.planId?.planName` resolved to `undefined` and calling
    // `.toLowerCase()` on it threw — crashing this whole filter as soon as
    // any specific plan tab was selected. Guard with `|| ""` first.
    let temp = filteredByTab.filter((client) =>
      activePlanTab === "all"
        ? true
        : (client.planId?.planName || "").toLowerCase() === activePlanTab
    );

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      temp = temp.filter((client) => {
        const shopName = client.shopid?.shopname?.toLowerCase() || "";
        const city = client.shopid?.address?.city?.toLowerCase() || "";
        const planName = client.planId?.planName?.toLowerCase() || "";
        const clientName = client.userid?.fullname?.toLowerCase() || "";
        return (
          shopName.includes(q) ||
          city.includes(q) ||
          planName.includes(q) ||
          clientName.includes(q)
        );
      });
    }

    return temp;
  }, [activeClientTab, activePlanTab, clients, searchQuery]);

  // Redirects to the client's shop profile page (used when clicking View Details / View Shop)
  const handleStartWork = async (item) => {
    if (!item) return;

    const rawShop = item.shopid || item.shopId || item.shop || item.shop_id;
    const shopObj = rawShop && typeof rawShop === "object" ? rawShop : null;
    const shopId =
      shopObj?._id ||
      shopObj?.id ||
      (typeof rawShop === "string" && rawShop.trim() ? rawShop.trim() : null) ||
      item.shopIdStr ||
      item.shop_id;

    if (!shopId) {
      console.error("Cannot redirect to shop profile: Shop ID is missing from client object", item);
      return;
    }

    if (item.hiredMarketer) {
      localStorage.setItem("MeAsmarketerId", item.hiredMarketer);
    }
    const userId =
      item.userid?._id ||
      item.userId?._id ||
      (typeof item.userid === "string"
        ? item.userid
        : typeof item.userId === "string"
        ? item.userId
        : null);
    if (userId) {
      localStorage.setItem("MeAsmarketerUserId", userId);
    }

    localStorage.setItem("OpenShopIdForMarketingByMarketer", shopId);
    localStorage.setItem("shopid", shopId);
    localStorage.setItem("previousRoute", "marketing-clients");

    const businessType =
      shopObj?.businessType || shopObj?._profileType || item.businessType;
    let route = businessTypeRoutes[businessType];

    if (!route && businessType) {
      const typeLower = String(businessType).toLowerCase();
      if (typeLower.includes("homemade")) route = businessTypeRoutes.homemadeShop;
      else if (typeLower.includes("gem") || typeLower.includes("mineral")) route = businessTypeRoutes.gemShop;
      else if (typeLower.includes("service")) route = businessTypeRoutes.services;
      else if (typeLower.includes("property")) route = businessTypeRoutes.businessPropertyCompany;
      else if (typeLower.includes("company") || typeLower.includes("jobs")) route = businessTypeRoutes.company;
      else if (typeLower.includes("useditem") || typeLower.includes("usedshop")) route = businessTypeRoutes.usedShop;
      else if (typeLower.includes("rawmaterial")) route = businessTypeRoutes.rawmaterialShop;
      else if (typeLower.includes("manufacturer")) route = businessTypeRoutes.manufacturerShop;
      else if (typeLower.includes("wholeseller") || typeLower.includes("wholesaler")) route = businessTypeRoutes.wholesellerShop;
      else if (typeLower.includes("retailer") || typeLower.includes("shop")) route = businessTypeRoutes.retailerShop;
      else if (typeLower.includes("recycle")) route = businessTypeRoutes.recycledShop;
      else if (typeLower.includes("farm")) route = businessTypeRoutes.farmingShop;
      else if (typeLower.includes("vehicle")) route = businessTypeRoutes.usedVehicleShop;
      else if (typeLower.includes("food") || typeLower.includes("restaurant")) route = businessTypeRoutes.foodShop;
      else if (typeLower.includes("transport")) route = businessTypeRoutes.transportShop;
    }

    if (!route) {
      route = "/NewItem-bussines-Profile/";
    }

    navigate(`${route}${shopId}`);
  };

  // "Start Work" button on a pending client: marks the task as in-progress
  // and redirects straight to that client's shop
  const handleBeginWork = async (item) => {
    try {
      await updateTaskStatus({
        purchaseId: item._id,
        taskStatus: "inProgress",
      }).unwrap();
    } catch (error) {
      console.error("FAILED TO START WORK:", error);
    }
    handleStartWork(item);
  };

  const handleViewStatistics = (client) => {
    const rawShop = client?.shopid || client?.shopId || client?.shop;
    const sid =
      (rawShop && typeof rawShop === "object" ? rawShop._id : rawShop) ||
      client?._id;
    if (sid) {
      localStorage.setItem("OpenShopIdForMarketingByMarketer", sid);
      localStorage.setItem("shopid", sid);
    }
    navigate(sid ? `/shop-statistics?shopid=${sid}` : "/shop-statistics");
  };

  return {
    activeClientTab,
    setActiveClientTab,
    activePlanTab,
    setActivePlanTab,
    searchQuery,
    setSearchQuery,
    show,
    setShow,
    isOpen,
    setIsOpen,
    isLoading,
    filteredClients,
    handleStartWork,
    handleBeginWork,
    handleViewStatistics,
  };
};
